import Collapse from 'blocks.simple/collapse/collapse'
import { cn } from 'ethcss'
import styles from '../settings.jcss'
import translate from 'core/translate'
import React, { useEffect, useState } from 'react'
import Button from 'blocks.simple/button/button'
import { Typography } from 'atoms/Typography'
import { grid, text as textStyles } from 'theme'
import { Radio } from 'atoms/Radio'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { updateCompany, updateUserSettings } from 'blocks.app/user/user.state'
import { SwitchButton } from '../../../atoms/SwitchButton'
import EditText from '../../../blocks.simple/editText/editText'
import helpers from '../../../core/helpers'
import { ICompany } from '../../../core/models/Company'

const MoveModalSettings = ({ company }: { company: ICompany }) => {
    const dispatch = useDispatch()
    const userMoveSettings = useSelector(
        (state: any) =>
            state.user.data.settings.digitalSignage || {
                groupScheduleMode: 'askEveryTime',
                overwriteSettings: 'askEveryTime',
            }
    )

    const [pingSettings, setPingSettings] = useState<any>(
        (company.dsPingSettings && company.dsPingSettings.dsConnectionTest) || {}
    )
    const [logsStorageDays, setLogsStorageDays] = useState<any>(
        (company.logsStorageDays && company.logsStorageDays.toString()) || '30'
    )
    const [moveSettings, setMoveSettings] = useState(userMoveSettings)

    const handleSwitchChange = () => {
        setPingSettings({
            ...pingSettings,
            enabled: !(pingSettings && pingSettings.enabled),
        })
    }

    const onChangeInterval = (interval: number) => {
        setPingSettings({
            ...pingSettings,
            interval,
        })
    }

    const onChangeLogsStorageTime = (value: string) => {
        setLogsStorageDays(value)
    }

    useEffect(() => {
        if (isEqual(userMoveSettings, moveSettings)) return
        dispatch(updateUserSettings({ data: { digitalSignage: moveSettings }, isSaveSettings: true }))
    }, [moveSettings])

    useEffect(() => {
        let timeout: any = null
        const companyPing = (company.dsPingSettings && company.dsPingSettings.dsConnectionTest) || {}
        const nextLogs = !isNaN(+logsStorageDays) ? +logsStorageDays : 30

        if (!isEqual(companyPing, pingSettings) || (company.logsStorageDays || 30) !== nextLogs) {
            timeout = setTimeout(() => {
                const interval =
                    pingSettings.interval < 15 ? 15 : pingSettings.interval > 720 ? 720 : pingSettings.interval

                dispatch(
                    updateCompany({
                        data: {
                            dsPingSettings: { dsConnectionTest: { enabled: pingSettings.enabled, interval } },
                            logsStorageDays: nextLogs,
                        },
                        isSaveCompany: true,
                    })
                )
            }, 500)
        }

        return () => clearTimeout(timeout)
    }, [pingSettings, logsStorageDays])

    return (
        <Collapse title={() => translate('devices')} className={cn(styles.background, grid.mb_md)}>
            <div className={styles.wrapper}>
                <div className={grid.mt_mdPlus}>
                    <Typography
                        type="title"
                        className={cn(textStyles.centerBlock, textStyles.up, textStyles['normalPlus'])}
                    >
                        {translate('scheduleForDevice')}
                    </Typography>
                    <Radio
                        className={cn(grid.mt_md)}
                        label={<Typography type="title">{translate('changeCurrentSchedule')}</Typography>}
                        selected={moveSettings.groupScheduleMode === 'apply'}
                        onClick={function () {
                            setMoveSettings({
                                ...moveSettings,
                                groupScheduleMode: 'apply',
                            })
                        }}
                    />
                    <Radio
                        className={cn(grid.mt_mini)}
                        label={<Typography type="title">{translate('remainCurrentSchedule')}</Typography>}
                        selected={moveSettings.groupScheduleMode === 'ignore'}
                        onClick={function () {
                            setMoveSettings({
                                ...moveSettings,
                                groupScheduleMode: 'ignore',
                            })
                        }}
                    />
                    <Radio
                        className={cn(grid.mt_mini)}
                        label={<Typography type="title">{translate('askEveryTime')}</Typography>}
                        selected={moveSettings.groupScheduleMode === 'askEveryTime'}
                        onClick={function () {
                            setMoveSettings({
                                ...moveSettings,
                                groupScheduleMode: 'askEveryTime',
                            })
                        }}
                    />
                </div>
                <div className={grid.mt_mdPlus}>
                    <Typography
                        type="title"
                        className={cn(textStyles.centerBlock, textStyles.up, textStyles['normalPlus'])}
                    >
                        {translate('groupAndDevicesSettingsOnMove')}
                    </Typography>
                    <Radio
                        className={cn(grid.mt_md)}
                        label={<Typography type="title">{translate('replaceWithParentSettings')}</Typography>}
                        selected={moveSettings.overwriteSettings === true}
                        onClick={function () {
                            setMoveSettings({
                                ...moveSettings,
                                overwriteSettings: true,
                            })
                        }}
                    />
                    <Radio
                        className={cn(grid.mt_mini)}
                        label={<Typography type="title">{translate('keepCurrentSettings')}</Typography>}
                        selected={moveSettings.overwriteSettings === false}
                        onClick={function () {
                            setMoveSettings({
                                ...moveSettings,
                                overwriteSettings: false,
                            })
                        }}
                    />
                    <Radio
                        className={cn(grid.mt_mini)}
                        label={<Typography type="title">{translate('askEveryTime')}</Typography>}
                        selected={moveSettings.overwriteSettings === 'askEveryTime'}
                        onClick={function () {
                            setMoveSettings({
                                ...moveSettings,
                                overwriteSettings: 'askEveryTime',
                            })
                        }}
                    />
                </div>
                {helpers.isEnabledPingSupport() && (
                    <div className={grid.mt_mdPlus}>
                        <Typography
                            type="title"
                            className={cn(textStyles.centerBlock, textStyles.up, textStyles['normalPlus'])}
                        >
                            {translate('autoCheckDSConnection')}
                        </Typography>
                        <div className={cn(grid.mt_md)}>
                            <div className={styles.optionCaption}>{translate('automaticallyCheckConnection')}</div>
                            <SwitchButton
                                onChange={handleSwitchChange}
                                checked={pingSettings && pingSettings.enabled}
                            />
                        </div>
                        <div
                            className={cn(
                                grid.mt_md,
                                !(moveSettings.dsConnectionTest && moveSettings.dsConnectionTest.enabled)
                                    ? styles.disabled
                                    : ''
                            )}
                        >
                            <div className={styles.optionCaption}>{translate('checkConnectionInterval')}</div>
                            <EditText
                                className={cn(styles.input)}
                                value={pingSettings.interval || 15}
                                mod="withLightBorder"
                                onChange={(interval: string) => {
                                    if (isNaN(+interval)) {
                                        return
                                    }

                                    onChangeInterval(+interval)
                                }}
                                disabled={!(pingSettings && pingSettings.enabled)}
                                autocomplete={'off'}
                            />
                        </div>
                    </div>
                )}
                <div className={grid.mt_mdPlus}>
                    <Typography
                        type="title"
                        className={cn(textStyles.centerBlock, textStyles.up, textStyles['normalPlus'])}
                    >
                        {translate('devicesLogs')}
                    </Typography>
                    <div className={cn(grid.mt_md)}>
                        <div className={styles.optionCaption}>{translate('devicesLogsStorageTime')}</div>
                        <EditText
                            className={cn(styles.input)}
                            value={logsStorageDays}
                            mod="withLightBorder"
                            type={'number'}
                            onChange={onChangeLogsStorageTime}
                            autocomplete={'off'}
                        />
                    </div>
                </div>
            </div>
        </Collapse>
    )
}

export default MoveModalSettings
